import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { adminAPI, apiClient, platformAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ComingSoonBadge } from './ComingSoon';
import SupportTenantSwitcher from './SupportTenantSwitcher';
import { MessageSquare } from 'lucide-react';
import { useOperatorAssistant } from './operator-assistant/OperatorAssistantContext';
import {
  HomeIcon,
  CalendarIcon,
  CreditCardIcon,
  ChartBarIcon,
  UsersIcon,
  UserGroupIcon,
  UserIcon,
  Cog6ToothIcon,
  TruckIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  ArrowUpTrayIcon,
  BellIcon,
  CommandLineIcon,
  BoltIcon,
  BuildingOffice2Icon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';

interface NavLink {
  path: string;
  label: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
  comingSoon?: boolean;
}

type NavbarProps = {
  hasSidebar?: boolean;
  isSidebarCollapsed?: boolean;
};

type CommandItem = {
  id: string;
  label: string;
  description: string;
  category: 'Navigate' | 'Actions' | 'Entities';
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  onSelect: () => void;
};

type GlobalAdminNotificationItem = {
  id: string;
  kind: 'billing_alert' | 'webhook_event';
  severity: 'warning' | 'error';
  title: string;
  description: string;
  timestamp?: string | Date | null;
};

function Navbar({ hasSidebar = false, isSidebarCollapsed = false }: NavbarProps) {
  const { user, logout } = useAuth();
  const assistant = useOperatorAssistant();
  const branding = useBranding();
  const { themePreference, effectiveTheme, cycleThemePreference } = useTheme();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [isSearchingEntities, setIsSearchingEntities] = useState(false);
  const [entityItems, setEntityItems] = useState<CommandItem[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [globalNotifications, setGlobalNotifications] = useState<GlobalAdminNotificationItem[]>([]);
  const [notificationLastUpdated, setNotificationLastUpdated] = useState<Date | null>(null);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [runtimeMode, setRuntimeMode] = useState<'dev' | 'prod' | 'default' | 'unknown'>('unknown');
  const paletteInputRef = useRef<HTMLInputElement | null>(null);
  const notificationPollRef = useRef<number | null>(null);

  const customerLinks: NavLink[] = [
    { path: '/', label: 'Dashboard', icon: HomeIcon },
    { path: '/booking', label: 'Book Vehicle', icon: CalendarIcon, comingSoon: true },
    { path: '/reservations', label: 'My Reservations', icon: CreditCardIcon, comingSoon: true },
    { path: '/billing', label: 'Billing & Invoices', icon: CreditCardIcon },
  ];

  const adminLinks: NavLink[] = [
    ...(assistant.eligible ? [{ path: '/admin/assistant', label: 'MODAL Assistant', icon: BoltIcon }] : []),
    { path: '/admin/ai-access', label: 'AI Access', icon: ShieldCheckIcon },
    { path: '/admin', label: 'Dashboard', icon: HomeIcon },
    { path: '/admin/analytics', label: 'Analytics', icon: ChartBarIcon },
    { path: '/admin/customers', label: 'Customers', icon: UsersIcon },
    { path: '/admin/accounts', label: 'Accounts', icon: BuildingOffice2Icon },
    { path: '/admin/quotes', label: 'Quotes', icon: DocumentTextIcon },
    { path: '/admin/reservations', label: 'Reservations', icon: CalendarIcon },
    { path: '/admin/farm-outs', label: 'Farm-outs', icon: CalendarIcon },
    { path: '/admin/dispatch', label: 'Dispatch', icon: CalendarIcon },
    { path: '/admin/vehicles', label: 'Vehicles', icon: TruckIcon },
    { path: '/admin/invoices', label: 'Invoices', icon: CreditCardIcon },
    { path: '/admin/invoice-import', label: 'Import', icon: ArrowUpTrayIcon },
    { path: '/admin/drivers', label: 'Drivers', icon: UserIcon },
    { path: '/admin/users', label: 'Users', icon: UsersIcon },
  ];

  const platformLinks: NavLink[] = [
    { path: '/admin/ai-access', label: 'AI Access', icon: ShieldCheckIcon },
    { path: '/admin', label: 'Dashboard', icon: HomeIcon },
    { path: '/platform/billing', label: 'Billing', icon: CreditCardIcon },
    { path: '/platform/invoices', label: 'Invoices', icon: CreditCardIcon },
    { path: '/platform/users', label: 'Users', icon: UsersIcon },
    { path: '/platform/support-staff', label: 'Support Staff', icon: ShieldCheckIcon },
    { path: '/platform/companies', label: 'Companies', icon: BuildingOfficeIcon },
  ];

  const isPlatformHost =
    typeof window !== 'undefined' &&
    window.location?.hostname?.toLowerCase().startsWith('globaladmin.');
  const showPlatformNav = isPlatformHost && Boolean((user as any)?.is_platform_admin);
  const showSupportTenantSwitcher = Boolean((user as any)?.is_support_staff);

  const tenantInfo = useMemo(() => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    const slug = hostname.includes('.') ? hostname.split('.')[0] : 'local';
    const companyName = branding.app_name?.trim();

    if (showPlatformNav) {
      return {
        label: 'Platform Account',
        value: companyName || 'ModalOS Platform',
      };
    }

    const normalizedSlug = slug || 'local';
    return {
      label: 'Company',
      value: companyName || normalizedSlug,
    };
  }, [branding.app_name, showPlatformNav]);

  const links = showPlatformNav
    ? platformLinks
    : user?.role === 'admin'
    ? adminLinks
    : customerLinks;

  const notificationBadgeCount = useMemo(
    () => globalNotifications.length,
    [globalNotifications],
  );

  const formatNotificationTimestamp = (value?: string | Date | null) => {
    if (!value) return 'just now';
    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) return 'just now';
    return timestamp.toLocaleString();
  };

  const loadGlobalAdminNotifications = useCallback(async () => {
    if (!showPlatformNav) {
      setGlobalNotifications([]);
      return;
    }

    setNotificationsLoading(true);
    setNotificationError(null);

    try {
      const [alertsResponse, webhookResponse] = await Promise.all([
        platformAPI.getBillingAlerts({
          limit: 5,
          page: 1,
          workflowStatus: 'open',
        }),
        platformAPI.getWebhookEvents({
          limit: 5,
          page: 1,
        }),
      ]);

      const alerts = Array.isArray(alertsResponse?.data?.alerts) ? alertsResponse.data.alerts : [];
      const webhookEvents = Array.isArray(webhookResponse?.data?.events) ? webhookResponse.data.events : [];

      const failedWebhookEvents = webhookEvents.filter(
        (event: any) => event?.status === 'failed' || event?.status === 'dead_letter',
      );

      const alertNotifications: GlobalAdminNotificationItem[] = alerts
        .filter((alert: any) => alert?.workflow_status !== 'resolved')
        .map((alert: any) => ({
          id: `billing-alert-${alert.id}`,
          kind: 'billing_alert',
          severity: alert?.severity === 'error' ? 'error' : 'warning',
          title: alert?.subject || alert?.alert_type || 'Billing alert',
          description: alert?.message || `Delivery status: ${alert?.delivery_status || 'unknown'}`,
          timestamp: alert?.created_at,
        }));

      const webhookNotifications: GlobalAdminNotificationItem[] = failedWebhookEvents.map((event: any) => ({
        id: `webhook-event-${event.id}`,
        kind: 'webhook_event',
        severity: event?.status === 'dead_letter' ? 'error' : 'warning',
        title: `Webhook ${event?.event_type || 'event'} ${String(event?.status || '').replaceAll('_', ' ')}`,
        description: event?.last_error || `Attempts: ${event?.attempts || 0}`,
        timestamp: event?.updated_at,
      }));

      const notifications = [...alertNotifications, ...webhookNotifications]
        .sort((a, b) => {
          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 8);

      setGlobalNotifications(notifications);
      setNotificationLastUpdated(new Date());
    } catch {
      setNotificationError('Unable to refresh billing notifications');
    } finally {
      setNotificationsLoading(false);
    }
  }, [showPlatformNav]);

  const baseCommands = useMemo<CommandItem[]>(() => {
    const navigationCommands = links
      .filter((link) => !link.comingSoon)
      .map((link) => ({
        id: `nav-${link.path}`,
        label: link.label,
        description: `Go to ${link.path}`,
        category: 'Navigate' as const,
        icon: link.icon,
        onSelect: () => navigate(link.path),
      }));

    const sharedActions: CommandItem[] = [
      {
        id: 'action-settings',
        label: 'Open Settings',
        description: 'Open admin settings',
        category: 'Actions',
        icon: Cog6ToothIcon,
        onSelect: () => navigate('/admin/settings'),
      },
      {
        id: 'action-logout',
        label: 'Log out',
        description: 'Sign out from this account',
        category: 'Actions',
        icon: ArrowRightOnRectangleIcon,
        onSelect: () => logout(),
      },
    ];

    const platformActions: CommandItem[] = showPlatformNav
      ? [
          {
            id: 'action-billing-health',
            label: 'Check billing health',
            description: 'Run platform billing health probe',
            category: 'Actions',
            icon: BoltIcon,
            onSelect: async () => {
              try {
                const response = await platformAPI.getBillingHealth();
                const status = response?.data?.status || 'ok';
                toast.success(`Billing health: ${status}`);
              } catch {
                toast.error('Unable to fetch billing health');
              }
            },
          },
          {
            id: 'action-open-billing',
            label: 'Open billing operations',
            description: 'Jump to platform billing operations',
            category: 'Actions',
            icon: CreditCardIcon,
            onSelect: () => navigate('/platform/billing'),
          },
          {
            id: 'action-open-companies',
            label: 'Open companies directory',
            description: 'Jump to platform companies',
            category: 'Actions',
            icon: BuildingOffice2Icon,
            onSelect: () => navigate('/platform/companies'),
          },
        ]
      : [];

    return [...navigationCommands, ...sharedActions, ...platformActions];
  }, [links, logout, navigate, showPlatformNav]);

  const allCommandItems = useMemo(
    () => [...baseCommands, ...entityItems],
    [baseCommands, entityItems],
  );

  const filteredCommandItems = useMemo(() => {
    const query = paletteQuery.trim().toLowerCase();
    if (!query) return allCommandItems;
    return allCommandItems.filter((item) =>
      `${item.label} ${item.description} ${item.category}`.toLowerCase().includes(query),
    );
  }, [allCommandItems, paletteQuery]);

  const executeCommand = async (item: CommandItem) => {
    setIsPaletteOpen(false);
    setPaletteQuery('');
    try {
      await item.onSelect();
    } catch {
      toast.error('Action failed');
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (isShortcut) {
        event.preventDefault();
        setIsPaletteOpen((current) => !current);
      }

      if (event.key === 'Escape') {
        setIsPaletteOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (isPaletteOpen) {
      setTimeout(() => paletteInputRef.current?.focus(), 0);
    }
  }, [isPaletteOpen]);

  useEffect(() => {
    if (!showPlatformNav) {
      setGlobalNotifications([]);
      setNotificationLastUpdated(null);
      setNotificationError(null);
      if (notificationPollRef.current) {
        window.clearInterval(notificationPollRef.current);
        notificationPollRef.current = null;
      }
      return;
    }

    loadGlobalAdminNotifications();

    notificationPollRef.current = window.setInterval(() => {
      loadGlobalAdminNotifications();
    }, 60_000);

    return () => {
      if (notificationPollRef.current) {
        window.clearInterval(notificationPollRef.current);
        notificationPollRef.current = null;
      }
    };
  }, [showPlatformNav, loadGlobalAdminNotifications]);

  useEffect(() => {
    if (!isPaletteOpen || !user || user.role !== 'admin') {
      setEntityItems([]);
      return;
    }

    const query = paletteQuery.trim();
    if (query.length < 2) {
      setEntityItems([]);
      return;
    }

    const timer = window.setTimeout(async () => {
      setIsSearchingEntities(true);

      const items: CommandItem[] = [];

      const getDataList = (data: any, keys: string[]) => {
        if (Array.isArray(data)) return data;
        for (const key of keys) {
          if (Array.isArray(data?.[key])) return data[key];
        }
        return [];
      };

      if (showPlatformNav) {
        const settled = await Promise.allSettled([
          platformAPI.getUsers({ search: query }),
          platformAPI.getCompanies(),
        ]);

        const platformUsersData = settled[0]?.status === 'fulfilled' ? (settled[0] as PromiseFulfilledResult<any>).value?.data : null;
        const companiesData = settled[1]?.status === 'fulfilled' ? (settled[1] as PromiseFulfilledResult<any>).value?.data : null;

        const platformUsers = getDataList(platformUsersData, ['users']);
        const companies = getDataList(companiesData, ['companies']);

        platformUsers.slice(0, 4).forEach((platformUser: any) => {
          items.push({
            id: `platform-user-${platformUser.id}`,
            label: platformUser.email || `User ${platformUser.id}`,
            description: 'Platform user entity',
            category: 'Entities',
            icon: UserGroupIcon,
            onSelect: () => navigate('/platform/users'),
          });
        });

        companies
          .filter((company: any) => {
            const text = `${company.name || ''} ${company.slug || ''}`.toLowerCase();
            return text.includes(query.toLowerCase());
          })
          .slice(0, 4)
          .forEach((company: any) => {
            items.push({
              id: `company-${company.id}`,
              label: company.name || company.slug || `Company ${company.id}`,
              description: 'Company entity',
              category: 'Entities',
              icon: BuildingOfficeIcon,
              onSelect: () => navigate('/platform/companies'),
            });
          });
      } else {
        const settled = await Promise.allSettled([
          adminAPI.getAccountCustomers({ search: query, limit: 5 }),
          adminAPI.getAllVehicles({ search: query, limit: 5 }),
          adminAPI.getAllDrivers({ search: query, limit: 5 }),
          adminAPI.getAccounts({ search: query, limit: 5 }),
        ]);

        const customersData = settled[0]?.status === 'fulfilled' ? (settled[0] as PromiseFulfilledResult<any>).value?.data : null;
        const vehiclesData = settled[1]?.status === 'fulfilled' ? (settled[1] as PromiseFulfilledResult<any>).value?.data : null;
        const driversData = settled[2]?.status === 'fulfilled' ? (settled[2] as PromiseFulfilledResult<any>).value?.data : null;
        const accountsData = settled[3]?.status === 'fulfilled' ? (settled[3] as PromiseFulfilledResult<any>).value?.data : null;

        const customers = getDataList(customersData, ['customers']);
        const vehicles = getDataList(vehiclesData, ['vehicles']);
        const drivers = getDataList(driversData, ['drivers']);
        const accounts = getDataList(accountsData, ['accounts']);

        customers.slice(0, 4).forEach((customer: any) => {
          const label = customer.display_name || customer.email || `Customer ${customer.id}`;
          const accountName: string | null = customer.account_name || null;
          items.push({
            id: `customer-${customer.id}`,
            label,
            description: accountName ? `Customer · ${accountName}` : 'Customer',
            category: 'Entities',
            icon: UsersIcon,
            onSelect: () => navigate('/admin/customers', {
              state: {
                openCustomer: customer,
                openCustomerSource: 'command-palette',
              },
            }),
          });
        });

        vehicles.slice(0, 4).forEach((vehicle: any) => {
          const label = [vehicle.make, vehicle.model, vehicle.license_plate || vehicle.licensePlate]
            .filter(Boolean)
            .join(' ')
            .trim() || `Vehicle ${vehicle.id}`;
          items.push({
            id: `vehicle-${vehicle.id}`,
            label,
            description: 'Vehicle entity',
            category: 'Entities',
            icon: TruckIcon,
            onSelect: () => navigate('/admin/vehicles'),
          });
        });

        drivers.slice(0, 4).forEach((driver: any) => {
          const label = [driver.firstName || driver.first_name, driver.lastName || driver.last_name]
            .filter(Boolean)
            .join(' ')
            .trim() || driver.email || `Driver ${driver.id}`;
          items.push({
            id: `driver-${driver.id}`,
            label,
            description: 'Driver',
            category: 'Entities',
            icon: UserIcon,
            onSelect: () => navigate('/admin/drivers'),
          });
        });

        accounts.slice(0, 4).forEach((account: any) => {
          const count: number | null = account.customer_count ?? null;
          items.push({
            id: `account-${account.id}`,
            label: account.name || `Account ${account.id}`,
            description: count != null ? `Account · ${count} customer${count !== 1 ? 's' : ''}` : 'Account',
            category: 'Entities',
            icon: BuildingOffice2Icon,
            onSelect: () => navigate(`/admin/accounts/${account.id}`),
          });
        });
      }

      setEntityItems(items);
      setIsSearchingEntities(false);
    }, 260);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isPaletteOpen, paletteQuery, showPlatformNav, user, navigate]);

  const sidebarAwareOffset = hasSidebar
    ? isSidebarCollapsed
      ? 'md:left-24'
      : 'md:left-72'
    : 'left-0';

  const themeButtonLabel = useMemo(() => {
    if (themePreference === 'system') {
      return `Theme: system (${effectiveTheme}). Click to switch to dark mode.`;
    }

    if (themePreference === 'dark') {
      return 'Theme: dark. Click to switch to light mode.';
    }

    return 'Theme: light. Click to follow system theme.';
  }, [effectiveTheme, themePreference]);

  const ThemeIcon = themePreference === 'system'
    ? ComputerDesktopIcon
    : effectiveTheme === 'dark'
      ? MoonIcon
      : SunIcon;

  const runtimeBadge = useMemo(() => {
    if (runtimeMode === 'dev') {
      return { label: 'DEV', className: 'bg-warning-container text-warning' };
    }

    if (runtimeMode === 'prod') {
      return { label: 'PROD', className: 'bg-success-container text-success' };
    }

    const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
    const isLikelyDevHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.localtest.me');

    return isLikelyDevHost
      ? { label: 'DEV', className: 'bg-warning-container text-warning' }
      : { label: 'UNKNOWN', className: 'bg-outline/50 text-on-surface-variant' };
  }, [runtimeMode]);

  useEffect(() => {
    let active = true;

    const fetchRuntimeMode = async () => {
      try {
        const response = await apiClient.get('/health/billing/readiness');
        const rawMode = String(response?.data?.runtime?.stripe_mode || '').toLowerCase();

        if (!active) return;

        if (rawMode === 'dev' || rawMode === 'development') {
          setRuntimeMode('dev');
          return;
        }

        if (rawMode === 'prod' || rawMode === 'production') {
          setRuntimeMode('prod');
          return;
        }

        if (rawMode === 'default') {
          setRuntimeMode('default');
          return;
        }

        setRuntimeMode('unknown');
      } catch {
        if (active) {
          setRuntimeMode('unknown');
        }
      }
    };

    fetchRuntimeMode();

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <nav className={`admin-shell-surface fixed top-0 right-0 z-50 ${sidebarAwareOffset} left-0`}>
        <div className="w-full mx-auto px-page-gutter">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              {showSupportTenantSwitcher && <SupportTenantSwitcher placement="navbar" />}
              <button
                onClick={() => setIsPaletteOpen(true)}
                className="admin-subpanel hidden md:flex items-center gap-3 rounded-card px-4 py-2.5 text-sm font-semibold text-on-surface transition-all hover:-translate-y-0.5"
                aria-label="Open command palette"
              >
                <CommandLineIcon className="h-5 w-5 text-primary" />
                <span>Open Command Palette</span>
                <span className="admin-subpanel rounded-md px-2 py-0.5 text-xs font-bold tracking-wide text-on-surface-variant">⌘K</span>
              </button>
              <span className={`hidden md:inline-flex items-center rounded-pill px-2.5 py-1 text-[11px] font-semibold tracking-wide ${runtimeBadge.className}`}>
                {runtimeBadge.label}
              </span>
              <button
                onClick={() => setIsPaletteOpen(true)}
                className="admin-subpanel md:hidden flex items-center gap-2 rounded-card px-3 py-2 text-sm font-semibold text-on-surface"
                aria-label="Open command palette"
              >
                <CommandLineIcon className="h-4 w-4 text-primary" />
                <span>Commands</span>
              </button>
              <span className={`md:hidden inline-flex items-center rounded-pill px-2 py-1 text-[10px] font-semibold tracking-wide ${runtimeBadge.className}`}>
                {runtimeBadge.label}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {assistant.eligible && (
                <button
                  type="button"
                  onClick={assistant.openDrawer}
                  className="admin-icon-button flex h-10 w-10 items-center justify-center rounded-md transition-colors"
                  aria-label="Open MODAL Assistant"
                  title="MODAL Assistant"
                >
                  <MessageSquare size={20} aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                onClick={cycleThemePreference}
                className="admin-icon-button flex items-center justify-center rounded-card p-2 transition-colors"
                aria-label={themeButtonLabel}
                title={themeButtonLabel}
              >
                <ThemeIcon className="h-5 w-5" />
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (!showPlatformNav) {
                      toast('No new notifications');
                      return;
                    }
                    setIsNotificationsOpen((current) => !current);
                  }}
                  className="admin-icon-button relative flex items-center justify-center rounded-card p-2 transition-colors"
                  aria-label="View notifications"
                >
                  <BellIcon className="h-5 w-5" />
                  {showPlatformNav && notificationBadgeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 rounded-full bg-error px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {notificationBadgeCount > 9 ? '9+' : notificationBadgeCount}
                    </span>
                  )}
                </button>

                {isNotificationsOpen && showPlatformNav && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsNotificationsOpen(false)} />
                    <div className="admin-table-card absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-card z-20">
                      <div className="admin-table-toolbar px-4 py-3">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-on-surface">Billing & Webhook Notifications</h3>
                          <button
                            onClick={() => loadGlobalAdminNotifications()}
                            className="text-xs font-medium text-primary hover:text-on-surface"
                            disabled={notificationsLoading}
                          >
                            Refresh
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-on-surface-variant">
                          {notificationLastUpdated ? `Updated ${notificationLastUpdated.toLocaleTimeString()}` : 'Waiting for initial sync'}
                        </p>
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        {notificationsLoading && globalNotifications.length === 0 && (
                          <div className="px-4 py-4 text-sm text-on-surface-variant">Loading notifications...</div>
                        )}

                        {!notificationsLoading && globalNotifications.length === 0 && !notificationError && (
                          <div className="px-4 py-4 text-sm text-on-surface-variant">No active billing or webhook notifications.</div>
                        )}

                        {notificationError && (
                          <div className="px-4 py-3 text-sm text-error">{notificationError}</div>
                        )}

                        {globalNotifications.map((notification) => (
                          <button
                            key={notification.id}
                            className="admin-table-row w-full px-4 py-3 text-left transition-colors"
                            onClick={() => {
                              setIsNotificationsOpen(false);
                              navigate('/platform/billing');
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <ExclamationTriangleIcon
                                className={`h-4 w-4 mt-0.5 ${notification.severity === 'error' ? 'text-error' : 'text-warning'}`}
                              />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-on-surface line-clamp-1 [line-clamp:1]">{notification.title}</p>
                                <p className="mt-0.5 text-xs text-on-surface-variant line-clamp-2 [line-clamp:2]">{notification.description}</p>
                                <p className="mt-1 text-[11px] text-on-surface-variant">
                                  {notification.kind === 'billing_alert' ? 'Billing alert' : 'Webhook event'} · {formatNotificationTimestamp(notification.timestamp)}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className="admin-table-toolbar px-4 py-3">
                        <button
                          className="w-full btn-secondary text-sm"
                          onClick={() => {
                            setIsNotificationsOpen(false);
                            navigate('/platform/billing');
                          }}
                        >
                          Open Billing Operations
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate('/admin/settings')}
                className="admin-icon-button flex items-center justify-center rounded-card p-2 transition-colors"
                aria-label="Open settings"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </button>

              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="admin-subpanel flex items-center gap-2 rounded-card px-3 py-2 text-sm text-on-surface transition-colors"
                >
                  <div className="w-8 h-8 text-white rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: 'var(--primary)' }}>
                    {user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden md:inline text-on-surface-variant">{user?.email}</span>
                  <ChevronDownIcon className="w-4 h-4 text-on-surface-variant" />
                </button>

                {isDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
                    <div className="admin-table-card absolute right-0 mt-2 w-64 rounded-card z-20">
                      <div className="py-1">
                        <div className="admin-table-toolbar px-4 py-3">
                          <div className="text-sm font-semibold text-on-surface">{user?.email}</div>
                          <div className="text-xs capitalize text-on-surface-variant">{user?.role}</div>
                          <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-on-surface-variant">
                            {tenantInfo.label}: <span className="text-on-surface">{tenantInfo.value}</span>
                          </div>
                        </div>

                        {user?.role === 'admin' && (
                          <Link
                            to="/admin/settings"
                            onClick={() => setIsDropdownOpen(false)}
                            className="admin-table-row flex items-center gap-2 px-4 py-2 text-sm text-on-surface transition-colors"
                          >
                            <Cog6ToothIcon className="w-4 h-4" />
                            <span>Settings</span>
                          </Link>
                        )}

                        {links.filter((l) => l.comingSoon).length > 0 && (
                          <>
                            <div className="my-1 mx-4 h-px bg-outline/40" />
                            {links.filter((l) => l.comingSoon).map(({ path, label, icon: Icon }) => (
                              <span
                                key={path}
                                className="flex items-center gap-2 px-4 py-2 text-sm text-on-surface-variant/70 cursor-not-allowed"
                              >
                                <Icon className="w-4 h-4" />
                                <span>{label}</span>
                                <ComingSoonBadge />
                              </span>
                            ))}
                          </>
                        )}

                        <div className="my-1 mx-4 h-px bg-outline/40" />
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            logout();
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error hover:bg-error-container transition-colors"
                        >
                          <ArrowRightOnRectangleIcon className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {isPaletteOpen && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm" onClick={() => setIsPaletteOpen(false)} />
          <div className="admin-table-card fixed inset-x-4 top-20 z-[71] mx-auto w-full max-w-2xl rounded-2xl">
            <div className="admin-table-toolbar p-3">
              <div className="admin-subpanel flex items-center gap-3 rounded-xl px-3 py-2">
                <CommandLineIcon className="h-5 w-5 text-on-surface-variant" />
                <input
                  ref={paletteInputRef}
                  type="text"
                  value={paletteQuery}
                  onChange={(event) => setPaletteQuery(event.target.value)}
                  className="w-full bg-transparent text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
                  placeholder="Search actions, pages, and entities..."
                />
                <span className="admin-subpanel rounded-md px-1.5 py-0.5 text-xs text-on-surface-variant">esc</span>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {isSearchingEntities && (
                <div className="px-3 py-2 text-xs text-on-surface-variant">Searching entities...</div>
              )}

              {!isSearchingEntities && filteredCommandItems.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-on-surface-variant">No results</div>
              )}

              {filteredCommandItems.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => executeCommand(item)}
                    className="admin-table-row mb-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-left"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="admin-subpanel rounded-lg p-1.5 text-on-surface-variant">
                        <ItemIcon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-on-surface">{item.label}</span>
                        <span className="block truncate text-xs text-on-surface-variant">{item.description}</span>
                      </span>
                    </div>
                    <span className="admin-subpanel rounded-pill px-2 py-0.5 text-[10px] uppercase tracking-wide text-on-surface-variant">
                      {item.category}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Navbar;
